<template>
  <header class="absolute top-0 left-0 right-0 z-50">
    <nav class="container-app">
      <div class="flex items-center justify-between h-20 mt-4">
        <!-- Logo -->
        <router-link to="/" class="flex-shrink-0">
          <img
            src="/logo.avif"
            alt="RECC - Real Estate Consulting Corp"
            class="h-16 md:h-20 w-auto"
          />
        </router-link>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-3">
          <!-- Inicio -->
          <a
            href="/#inicio"
            :class="[
              'px-8 py-2.5 text-sm font-semibold rounded-full transition-all duration-200',
              activeSection === 'inicio'
                ? 'bg-accent-400 text-neutral-800'
                : 'bg-white/90 text-neutral-700 hover:bg-accent-400 hover:text-neutral-800'
            ]"
          >
            Inicio
          </a>

          <!-- Servicios con dropdown -->
          <div
            class="relative"
            @mouseenter="isServiciosOpen = true"
            @mouseleave="isServiciosOpen = false"
          >
            <a
              href="/#servicios"
              :class="[
                'px-8 py-2.5 text-sm font-semibold rounded-full transition-all duration-200 inline-block',
                activeSection === 'servicios'
                  ? 'bg-accent-400 text-neutral-800'
                  : 'bg-white/90 text-neutral-700 hover:bg-accent-400 hover:text-neutral-800'
              ]"
            >
              Servicios
            </a>

            <!-- Dropdown -->
            <transition name="fade">
              <div
                v-if="isServiciosOpen"
                class="absolute top-full left-1/2 -translate-x-1/2 pt-2"
              >
                <div class="flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-2 py-1.5 shadow-lg">
                  <router-link
                    v-for="sub in serviciosLinks"
                    :key="sub.path"
                    :to="sub.path"
                    class="px-5 py-2 text-xs font-semibold rounded-full whitespace-nowrap transition-all duration-200 bg-white text-neutral-700 hover:bg-accent-400 hover:text-neutral-800"
                  >
                    {{ sub.name }}
                  </router-link>
                </div>
              </div>
            </transition>
          </div>

          <!-- Contacto -->
          <a
            href="/#contacto"
            :class="[
              'px-8 py-2.5 text-sm font-semibold rounded-full transition-all duration-200',
              activeSection === 'contacto'
                ? 'bg-accent-400 text-neutral-800'
                : 'bg-white/90 text-neutral-700 hover:bg-accent-400 hover:text-neutral-800'
            ]"
          >
            Contacto
          </a>
        </div>

        <!-- Mobile Menu Button -->
        <div class="flex items-center md:hidden">
          <button
            :class="[
              'p-2 rounded-lg transition-colors duration-200',
              isOnDarkPage ? 'hover:bg-white/20' : 'hover:bg-white/30'
            ]"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
            aria-label="Abrir menú"
          >
            <svg
              v-if="!isMobileMenuOpen"
              :class="['w-7 h-7', isOnDarkPage ? 'text-white' : 'text-neutral-700']"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg
              v-else
              :class="['w-7 h-7', isOnDarkPage ? 'text-white' : 'text-neutral-700']"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu -->
    <transition name="slide-down">
      <div
        v-if="isMobileMenuOpen"
        class="md:hidden bg-white/95 backdrop-blur-sm shadow-lg"
      >
        <div class="px-4 py-6 space-y-2">
          <a
            href="/#inicio"
            class="block px-4 py-3 text-lg font-semibold rounded-lg transition-colors duration-200 text-neutral-700 hover:bg-accent-400 hover:text-neutral-800"
            @click="isMobileMenuOpen = false"
          >
            Inicio
          </a>

          <!-- Servicios con sub-links en móvil -->
          <a
            href="/#servicios"
            class="block px-4 py-3 text-lg font-semibold rounded-lg transition-colors duration-200 text-neutral-700 hover:bg-accent-400 hover:text-neutral-800"
            @click="isMobileMenuOpen = false"
          >
            Servicios
          </a>
          <router-link
            v-for="sub in serviciosLinks"
            :key="sub.path"
            :to="sub.path"
            class="block px-8 py-2 text-base font-medium rounded-lg transition-colors duration-200 text-neutral-600 hover:bg-accent-400 hover:text-neutral-800"
            @click.native="isMobileMenuOpen = false"
          >
            {{ sub.name }}
          </router-link>

          <a
            href="/#contacto"
            class="block px-4 py-3 text-lg font-semibold rounded-lg transition-colors duration-200 text-neutral-700 hover:bg-accent-400 hover:text-neutral-800"
            @click="isMobileMenuOpen = false"
          >
            Contacto
          </a>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
export default {
  name: 'TheHeader',
  data() {
    return {
      isMobileMenuOpen: false,
      isServiciosOpen: false,
      activeSection: 'inicio',
      serviciosLinks: [
        { name: 'Rentas seguras', path: '/polizas' },
        { name: 'Contable', path: '/contable' },
        { name: 'Gestión inmobiliaria', path: '/gestion' }
      ],
      serviciosPaths: ['/polizas', '/contable', '/gestion']
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
    this.checkServiceRoute();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  watch: {
    '$route'() {
      this.checkServiceRoute();
    }
  },
  computed: {
    isOnDarkPage() {
      return this.serviciosPaths.includes(this.$route.path);
    }
  },
  methods: {
    checkServiceRoute() {
      if (this.serviciosPaths.includes(this.$route.path)) {
        this.activeSection = 'servicios';
      }
    },
    onScroll() {
      if (this.serviciosPaths.includes(this.$route.path)) {
        this.activeSection = 'servicios';
        return;
      }
      const sections = ['contacto', 'servicios', 'inicio'];
      for (let i = 0; i < sections.length; i++) {
        const el = document.getElementById(sections[i]);
        if (el && el.getBoundingClientRect().top <= 150) {
          this.activeSection = sections[i];
          return;
        }
      }
      this.activeSection = 'inicio';
    }
  }
};
</script>
